import { ReactNode, useCallback } from "react";
import { Bill } from "@common/types/bill";
import {
    ScheduleDeleteJournalEntryMode,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import { useScheduleChange } from "@components/accounting/schedules/change/useScheduleChange";
import {
    opensScheduleModalBeforeApply,
    SchedulePickerTemplateWithAnchors,
} from "@components/accounting/schedules/scheduleApplyConfirmHelpers";
import { useOpenScheduleJournalEntries } from "@components/accounting/schedules/useOpenScheduleJournalEntries";
import { useScheduleLifecycleRequests } from "@components/accounting/schedules/useScheduleLifecycleRequests";
import { useRemoveInvoiceSchedule } from "@components/invoices/useRemoveInvoiceSchedule";
import { InteractiveScheduleCellState } from "@components/invoices/views/scheduleCellState";
import { BillScheduleReplaceScope } from "./billScheduleCellState";
import { BillScheduleCallbacks } from "./useBillScheduleCallbacks";
import { useApplyBillSchedule } from "./useApplyBillSchedule";

/**
 * The apply, replace, remove and lifecycle wiring a bill Schedule cell needs,
 * shared by the bill and bill line cells. `scope` is what an apply covers:
 * the whole bill, the lines the current schedule owns, or one line.
 */
export function useBillSchedulePicker(options: {
    bill: Bill | undefined;
    state: InteractiveScheduleCellState | null;
    scope: BillScheduleReplaceScope & { sourceLabel: string };
    callbacks: Partial<BillScheduleCallbacks> | undefined;
}) {
    const { bill, state, scope, callbacks } = options;
    const onScheduleApplied = callbacks?.onScheduleApplied;
    const onRequestScheduleConfirm = callbacks?.onRequestScheduleConfirm;
    const entityId = bill?.entityId;
    const appliedState = state?.kind === "applied" ? state : null;

    const {
        applySchedule: runApply,
        isApplying,
        linkedConflictModal,
    } = useApplyBillSchedule({
        showLockedPeriods: callbacks?.showLockedPeriods,
    });
    const { removeSchedule, isRemoving } = useRemoveInvoiceSchedule({
        onRemoved: onScheduleApplied,
        showLockedPeriods: callbacks?.showLockedPeriods,
    });

    const applySchedule = useCallback(
        async (template: SchedulePickerTemplateWithAnchors) => {
            if (!bill) {
                return false;
            }
            const amount = Math.abs(scope.amount);
            if (opensScheduleModalBeforeApply(template)) {
                return (
                    (await onRequestScheduleConfirm?.({
                        bill,
                        template,
                        sourceLineIds: scope.sourceLineIds,
                        amount,
                        sourceLabel: scope.sourceLabel,
                    })) ?? false
                );
            }
            return await runApply({
                templateId: template.id,
                target: {
                    entityId: bill.entityId,
                    billId: bill.id,
                    amount,
                    sourceLineIds: scope.sourceLineIds,
                },
                onApplied: onScheduleApplied,
            });
        },
        [bill, onRequestScheduleConfirm, onScheduleApplied, runApply, scope],
    );

    const removeCurrent = useCallback(
        async (journalEntryMode: ScheduleDeleteJournalEntryMode) => {
            if (appliedState == null || entityId == null) {
                return false;
            }
            return await removeSchedule({
                entityId,
                scheduleApplicationId: appliedState.scheduleApplicationId,
                journalEntryMode,
            });
        },
        [appliedState, entityId, removeSchedule],
    );

    const { onSelect, onClear, confirmModal } = useScheduleChange({
        source: "bill",
        current: appliedState
            ? {
                  scheduleApplicationId: appliedState.scheduleApplicationId,
                  scheduleTemplateId: appliedState.scheduleTemplateId,
                  scheduleName: appliedState.scheduleName,
              }
            : null,
        applySchedule,
        removeCurrent,
    });

    const { onChangeSchedule, onResumeSchedule } = useScheduleLifecycleRequests(
        {
            applicationId: appliedState?.scheduleApplicationId ?? null,
            status: appliedState?.status ?? null,
            entityId,
            sourceType: ScheduleSourceType.bill,
            requestLifecycleAction: callbacks?.onRequestLifecycleAction,
        },
    );
    const onViewSchedule = useOpenScheduleJournalEntries(
        appliedState
            ? {
                  templateId: appliedState.scheduleTemplateId,
                  scheduleApplicationId: appliedState.scheduleApplicationId,
                  scheduleType: ScheduleType.expense,
              }
            : null,
    );

    const onCreateSchedule = callbacks?.onCreateSchedule;
    const overlays: ReactNode = (
        <>
            {confirmModal}
            {linkedConflictModal}
        </>
    );

    return {
        pickerProps: {
            isDisabled: isApplying || isRemoving,
            onSelect,
            onClear,
            onChangeSchedule,
            onResumeSchedule,
            onViewSchedule,
            onCreate:
                onCreateSchedule && entityId != null
                    ? () => onCreateSchedule({ entityId })
                    : undefined,
        },
        overlays,
    };
}
